import { useEffect, useRef, useState } from "react";

import { formatBytes, formatDateTime, formatRelative, plateTypeLabel } from "@/lib/format";
import { api, type AssetListItem, type NotesPreviewResponse, type PlateColor } from "@/lib/api";
import { comparacaoDeRender, formatarSegundos, segundosDoRender } from "@/lib/proofscopeRender";
import { useFileManagerStore } from "@/state/fileManagerStore";

import { ThumbnailWithPins } from "./ThumbnailWithPins";

interface NotesSidePanelProps {
  asset: AssetListItem;
}

export function NotesSidePanel({ asset }: NotesSidePanelProps) {
  const selectedNoteId = useFileManagerStore((state) => state.selectedNoteId);
  const setSelectedNoteId = useFileManagerStore((state) => state.setSelectedNoteId);
  const setSelectedAssetId = useFileManagerStore((state) => state.setSelectedAssetId);

  const createdMs = new Date(asset.created_at).getTime();
  const lastMs = new Date(asset.last_accessed_at).getTime();
  const showLastViewed =
    Number.isFinite(createdMs) && Number.isFinite(lastMs) && lastMs > createdMs;

  const [data, setData] = useState<NotesPreviewResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const maskSource = data?.cache_metrics?.mask_source;
  const maskTitle =
    data?.cache_metrics && maskSource !== "unknown"
      ? `${data.cache_metrics.mask_page_count} página(s), ${data.cache_metrics.mask_object_count} arquivo(s), fonte ${maskSource}`
      : undefined;
  // O tempo de render mora aqui, e não no painel técnico do viewer (03/set):
  // quem abre o arquivo quer a arte; quem compara os dois sistemas está no
  // manager. O nosso número prefere `render.timings` (tempo de trabalho, sem
  // fila — a mesma régua que o viewer usava) e cai na duração do job, que é o
  // que este painel sempre mostrou, quando o render ainda não publica timings.
  const nossoRender = segundosDoRender(data?.render_timings) ?? asset.render_seconds ?? null;
  const renderDeles = data?.proofscope?.render_seconds ?? null;
  const nossoRenderLabel = formatarSegundos(nossoRender);
  const renderDelesLabel = formatarSegundos(renderDeles);
  const comparacao = comparacaoDeRender(nossoRender, renderDeles);

  useEffect(() => {
    let cancelled = false;

    setLoading(true);
    setError(null);
    api
      .getNotesPreview(asset.id, 1)
      .then((nextData) => {
        if (!cancelled) setData(nextData);
      })
      .catch((err) => {
        if (!cancelled) setError(err instanceof Error ? err.message : String(err));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [asset.id]);

  useEffect(() => {
    if (!selectedNoteId) return;
    const noteElement = listRef.current?.querySelector(
      `[data-note-id="${selectedNoteId}"]`,
    );
    noteElement?.scrollIntoView({ behavior: "smooth", block: "nearest" });
  }, [selectedNoteId]);

  return (
    <aside className="flex w-96 shrink-0 flex-col border-l border-ink-700 bg-ink-900/50">
      <header className="flex items-start justify-between gap-2 border-b border-ink-700 px-4 py-3">
        <div className="min-w-0 space-y-1">
          <div className="truncate text-sm font-semibold" title={asset.filename}>
            {asset.filename}
          </div>
          <div className="text-xs text-ink-300">
            {asset.notes_count} {asset.notes_count === 1 ? "nota" : "notas"}
          </div>
        </div>
        <button
          type="button"
          aria-label="Fechar painel do arquivo"
          title="Fechar"
          onClick={() => {
            setSelectedNoteId(null);
            setSelectedAssetId(null);
          }}
          className="shrink-0 rounded p-1 text-ink-400 hover:bg-ink-800 hover:text-ink-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500"
        >
          <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" aria-hidden="true">
            <path d="m6 6 12 12M18 6 6 18" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
          </svg>
        </button>
      </header>

      <div className="border-b border-ink-700 px-4 py-3">
        <ThumbnailWithPins
          thumbUrl={data?.thumb_url ?? asset.thumb_url}
          widthMm={data?.thumb_width_mm ?? null}
          heightMm={data?.thumb_height_mm ?? null}
          notes={data?.notes ?? []}
        />
        <dl className="mt-3 space-y-1 rounded border border-ink-800 bg-ink-950/40 p-2 text-xs">
          <div className="flex justify-between gap-3">
            <dt className="shrink-0 text-ink-500">Tamanho</dt>
            <dd className="truncate text-ink-200">{formatBytes(asset.size_bytes)}</dd>
          </div>
          <div className="flex justify-between gap-3">
            <dt className="shrink-0 text-ink-500">Máscaras</dt>
            <dd className="truncate text-ink-200" title={maskTitle}>
              {formatBytes(data?.cache_metrics?.mask_bytes_total)}
            </dd>
          </div>
          <div className="flex justify-between gap-3">
            <dt className="shrink-0 text-ink-500">Tempo de render</dt>
            <dd
              className="truncate tabular-nums text-ink-200"
              title={nossoRender != null ? `${nossoRender} s neste visualizador` : undefined}
            >
              {nossoRenderLabel ?? "—"}
            </dd>
          </div>
          {renderDelesLabel ? (
            <div className="flex justify-between gap-3">
              <dt className="shrink-0 text-ink-500">Render no ProofScope</dt>
              <dd className="truncate tabular-nums text-ink-200">{renderDelesLabel}</dd>
            </div>
          ) : null}
          {comparacao ? (
            <div className="flex justify-between gap-3">
              <dt className="shrink-0 text-ink-500">Comparação</dt>
              <dd className="truncate text-ink-200">{comparacao}</dd>
            </div>
          ) : null}
          <div className="flex justify-between gap-3">
            <dt className="shrink-0 text-ink-500">Data de criação</dt>
            <dd className="truncate text-ink-200" title={new Date(asset.created_at).toLocaleString("pt-BR")}>
              {formatDateTime(asset.created_at)}
            </dd>
          </div>
          {showLastViewed && (
            <div className="flex justify-between gap-3">
              <dt className="shrink-0 text-ink-500">Última visualização</dt>
              <dd
                className="truncate text-ink-200"
                title={new Date(asset.last_accessed_at).toLocaleString("pt-BR")}
              >
                {formatDateTime(asset.last_accessed_at)}
              </dd>
            </div>
          )}
          <div className="flex justify-between gap-3">
            <dt className="shrink-0 text-ink-500">Dimensões (px)</dt>
            <dd className="truncate text-ink-200">
              {data?.thumb_width_px != null && data?.thumb_height_px != null
                ? `${data.thumb_width_px} × ${data.thumb_height_px}`
                : "—"}
            </dd>
          </div>
          <div className="flex justify-between gap-3">
            <dt className="shrink-0 text-ink-500">Dimensões (mm)</dt>
            <dd className="truncate text-ink-200">
              {data?.thumb_width_mm != null && data?.thumb_height_mm != null
                ? `${data.thumb_width_mm.toFixed(1)} × ${data.thumb_height_mm.toFixed(1)}`
                : "—"}
            </dd>
          </div>
          <div className="flex justify-between gap-3">
            <dt className="shrink-0 text-ink-500">DPI</dt>
            <dd className="truncate text-ink-200">{asset.render_dpi ?? "—"}</dd>
          </div>
          <div className="space-y-0.5">
            <dt className="text-ink-500">Path</dt>
            <dd className="break-all text-ink-200">{asset.path}</dd>
          </div>
        </dl>
        <div className="mt-3 space-y-1">
          <div className="text-[10px] uppercase tracking-wide text-ink-500">Cores</div>
          <ColorList plates={asset.plates} />
        </div>
      </div>

      <div ref={listRef} className="flex-1 space-y-2 overflow-auto px-3 py-2 scrollbar-thin">
        {loading && <div className="text-xs text-ink-400">Carregando notas...</div>}
        {error && <div className="text-xs text-red-400">{error}</div>}
        {data && data.notes.length === 0 && (
          <div className="px-1 py-2 text-xs text-ink-500">Sem notas nesta página.</div>
        )}
        {data?.notes.map((note) => {
          const active = note.id === selectedNoteId;

          return (
            <button
              type="button"
              key={note.id}
              data-note-id={note.id}
              onClick={() => setSelectedNoteId(active ? null : note.id)}
              className={`w-full cursor-pointer rounded border p-2 text-left text-xs transition ${
                active
                  ? "border-emerald-500 bg-emerald-900/20"
                  : "border-ink-700 hover:bg-ink-800/60"
              }`}
            >
              <div className="mb-1 flex items-center justify-between gap-2">
                <span className="truncate font-medium text-ink-100">{note.author}</span>
                <span className="shrink-0 text-ink-500">{formatRelative(note.created_at)}</span>
              </div>
              <div className="line-clamp-3 whitespace-pre-wrap text-ink-200">{note.body}</div>
            </button>
          );
        })}
      </div>
    </aside>
  );
}

interface ColorListProps {
  plates: PlateColor[];
}

function ColorList({ plates }: ColorListProps) {
  if (plates.length === 0) {
    return <div className="text-[10px] text-ink-500">sem cores detectadas</div>;
  }

  return (
    <ul className="space-y-1">
      {plates.map((plate) => (
        <li key={plate.slug} className="flex items-center gap-2 text-xs">
          <span
            className="h-4 w-4 shrink-0 rounded-sm ring-1 ring-ink-600"
            style={{ backgroundColor: plate.rgb }}
            aria-hidden="true"
          />
          <span className="min-w-0 flex-1 truncate" title={plate.name}>
            {plate.name}
          </span>
          <span className="shrink-0 text-[10px] text-ink-500">{plateTypeLabel(plate.type)}</span>
        </li>
      ))}
    </ul>
  );
}
